// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { ApiParameter, get } from "@insite/client-framework/Services/ApiService";
import { CatalogPageModel, CategoryModel } from "@insite/client-framework/Types/ApiModels";

export interface GetCatalogPageByPathApiParameter extends ApiParameter {
    path: string;
}

const catalogPagesUrl = "/api/v1/catalogPages";

export type CatalogPage = Omit<CatalogPageModel, "category"> & {
    categoryId?: string;
    categoryIdWithBrandId?: string;
};

export interface CatalogPageResult {
    catalogPage: CatalogPage;
    categoriesById: SafeDictionary<Category>;
}

export type Category = Omit<CategoryModel, "subCategories"> & {
    subCategoryIds?: string[];
};

export async function getCatalogPageByPath(parameter: GetCatalogPageByPathApiParameter): Promise<CatalogPageResult> {
    const catalogPageModel = await get<CatalogPageModel>(`${catalogPagesUrl}`, parameter);
    const category = catalogPageModel.category ?? undefined;
    delete (catalogPageModel as Partial<typeof catalogPageModel>)?.["category"];

    (catalogPageModel as CatalogPage).categoryId = category?.id;

    if (catalogPageModel.brandId && category) {
        addBrandToId(category, catalogPageModel.brandId);
        (catalogPageModel as CatalogPage).categoryIdWithBrandId = category.id;
    }

    const categoriesById: SafeDictionary<Category> = {};
    if (category) {
        cleanCategory(category, categoriesById, 1);
    }

    return {
        catalogPage: catalogPageModel,
        categoriesById,
    };
}

// when we retrieve a category in the context of a brand, some of the properties may be different. We store them by categoryId:brandId
// so that everything on the site just works
function addBrandToId(category: CategoryModel, brandId: string) {
    category.id = `${category.id}:${brandId}`;
    category.subCategories?.forEach(o => addBrandToId(o, brandId));
}

function cleanCategory(category: CategoryModel, categoriesById: SafeDictionary<Category>, subCategoryLevels: number) {
    const subCategories = category.subCategories;
    subCategories?.sort((a, b) => a.sortOrder - b.sortOrder || a.shortDescription.localeCompare(b.shortDescription));
    delete (category as Partial<typeof category>)?.["subCategories"];
    const actualCategory = category as any as Category;
    if (subCategoryLevels > 0 && subCategories) {
        const subCategoryIds = [];
        for (const subCategory of subCategories) {
            subCategoryIds.push(subCategory.id);
            cleanCategory(subCategory, categoriesById, subCategoryLevels - 1);
            // xng-change: remove the line below to fix https://xngage.atlassian.net/browse/SAINC-1326
            // delete subCategory.htmlContent;
        }
        actualCategory.subCategoryIds = subCategoryIds;
    }

    categoriesById[category.id] = actualCategory;
}
